/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent } from 'react'
import styled, { useTheme } from 'styled-components/native'

import { ColorsType, TextColorKey } from 'theme/types'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { PasswordRuleStatus } from 'ui/designSystem/PasswordInput/enums'
import { Invalidate } from 'ui/svg/icons/Invalidate'
import { AccessibleIcon } from 'ui/svg/icons/types'
import { Validate } from 'ui/svg/icons/Validate'
import { Typo } from 'ui/theme'

type PasswordRuleConfig = {
  textColor: TextColorKey
  iconColor: ColorsType
  Icon?: FunctionComponent<AccessibleIcon>
}

type Props = {
  title: string
  status: PasswordRuleStatus
}

export const PasswordRule: FunctionComponent<Props> = ({ title, status }) => {
  const { designSystem } = useTheme()

  const config: Record<PasswordRuleStatus, PasswordRuleConfig> = {
    [PasswordRuleStatus.Default]: {
      textColor: 'disabled',
      iconColor: designSystem.color.icon.disabled,
      Icon: undefined,
    },
    [PasswordRuleStatus.Valid]: {
      textColor: 'success',
      iconColor: designSystem.color.icon.success,
      Icon: Validate,
    },
    [PasswordRuleStatus.Invalid]: {
      textColor: 'error',
      iconColor: designSystem.color.icon.error,
      Icon: Invalidate,
    },
  }

  const { textColor, iconColor, Icon } = config[status]

  return (
    <StyledView gap={1}>
      <StyledCaption color={textColor}>{title}</StyledCaption>
      {Icon ? (
        <IconContainer>
          <Icon color={iconColor} size={designSystem.size.icon.s} />
        </IconContainer>
      ) : null}
    </StyledView>
  )
}

const IconContainer = styled.View({
  flexShrink: 0,
})

const StyledView = styled(ViewGap)(({ theme }) => ({
  flexDirection: 'row-reverse',
  maxWidth: theme.forms.maxWidth,
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
}))

const StyledCaption = styled(Typo.BodyAccentXs)(({ theme }) => ({
  paddingLeft: theme.designSystem.size.spacing.xs,
  flexShrink: 1,
}))
